import {
  Button,
  ButtonLink,
  ListItem,
  Modal,
  ModalProps,
  Select,
  Stack,
  Text,
  useModals,
} from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface ApplicantDataProtectionSenderModalProps extends ModalProps {
  emails: string[];
  requestTitle: string;
  onSend: (fromEmail: string) => Promise<boolean>;
}

export function ApplicantDataProtectionSenderModal({
  emails,
  requestTitle,
  onSend,
  ...props
}: ApplicantDataProtectionSenderModalProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const [fromEmail, setFromEmail] = useState(emails[0] ?? "");
  const [isSending, setIsSending] = useState(false);
  const hasNoEmailIntegrations = emails.length === 0;

  const handleSend = async () => {
    if (isSending || !emails.includes(fromEmail)) return;
    setIsSending(true);
    try {
      if (await onSend(fromEmail)) close(props.id);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Modal
      {...props}
      title={requestTitle}
      withCloser
      actions={
        hasNoEmailIntegrations ? undefined : (
          <Stack>
            <Button
              onClick={handleSend}
              isLoading={isSending}
              disabled={!emails.includes(fromEmail)}
              size="lg"
            >
              {t("core:send")}
            </Button>
          </Stack>
        )
      }
    >
      {hasNoEmailIntegrations ? (
        <ListItem
          preTitle={t("core:email_from")}
          titleSlot={
            <Text className="text-danger font-semibold">
              {t("ats:email_integration_missing_title")}
            </Text>
          }
          caption={t("ats:email_integration_missing_description")}
          valueSlot={
            <ButtonLink href="/account-settings/integrations" blank variant="secondary">
              {t("integrations:connect")}
            </ButtonLink>
          }
        />
      ) : (
        <Select
          label={t("core:email_from")}
          emptyTitle={t("common:select")}
          options={emails.map((email) => ({ id: email, title: email }))}
          value={fromEmail}
          onChange={(value) => setFromEmail(value ? String(value) : "")}
          disabled={isSending}
          required
        />
      )}
    </Modal>
  );
}
